import { useEffect, useRef } from 'react';

interface MentionSuggestion {
  type: 'user' | 'group' | 'builtin';
  id: string;
  displayName: string;
  slug: string;
  avatarUrl?: string | null;
}

interface MentionAutocompleteProps {
  suggestions: MentionSuggestion[];
  selectedIndex: number;
  onSelect: (suggestion: MentionSuggestion) => void;
}

export function MentionAutocomplete({ suggestions, selectedIndex, onSelect }: MentionAutocompleteProps) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const selected = listRef.current?.children[selectedIndex] as HTMLElement | undefined;
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (suggestions.length === 0) return null;

  return (
    <div
      ref={listRef}
      className="absolute bottom-full left-0 right-0 mb-1 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800"
      role="listbox"
    >
      {suggestions.map((s, i) => (
        <button
          key={`${s.type}-${s.id}`}
          role="option"
          aria-selected={i === selectedIndex}
          className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
            i === selectedIndex
              ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
              : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
          }`}
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(s);
          }}
        >
          {s.type === 'user' && s.avatarUrl ? (
            <img src={s.avatarUrl} alt="" className="h-6 w-6 rounded-full object-cover" />
          ) : s.type === 'user' ? (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-600 dark:bg-gray-600 dark:text-gray-300">
              {s.displayName.charAt(0).toUpperCase()}
            </div>
          ) : s.type === 'group' ? (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-100 text-xs font-medium text-purple-600 dark:bg-purple-900 dark:text-purple-300">
              G
            </div>
          ) : (
            <div className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-xs font-medium text-amber-600 dark:bg-amber-900 dark:text-amber-300">
              @
            </div>
          )}
          <div className="flex-1 truncate">
            <span className="font-medium">{s.displayName}</span>
            <span className="ml-1 text-gray-400 dark:text-gray-500">@{s.slug}</span>
          </div>
          <span className="text-xs text-gray-400 capitalize dark:text-gray-500">{s.type}</span>
        </button>
      ))}
    </div>
  );
}
